import { useState } from "react";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CalendarClock } from "lucide-react";
import { CHECKBOX_CLS, errorText, to24h } from "@/lib/calendar";

export const RescheduleSection = ({ ride, guard, onChanged }) => {
  const hasReturn = ride.service_type === "round_trip" && !ride.wait_and_return;
  const [form, setForm] = useState({
    pickup_date: ride.pickup_date || "",
    pickup_time: to24h(ride.pickup_time),
    return_pickup_date: ride.return_pickup_date || ride.pickup_date || "",
    return_pickup_time: to24h(ride.return_pickup_time),
    return_time_tbc: !!ride.return_time_tbc || (hasReturn && !ride.return_pickup_time),
    special_requests: ride.special_requests || "",
    dispatch_notes: ride.dispatch_notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const editable = ["requested", "approved", "scheduled"].includes(ride.status);

  const save = async () => {
    const body = { ...form };
    if (!hasReturn) {
      delete body.return_pickup_date; delete body.return_pickup_time; delete body.return_time_tbc;
    } else if (body.return_time_tbc) {
      delete body.return_pickup_time;
    }
    setBusy(true);
    try {
      const res = await guard((force) => apiClient.put(`/rides/${ride.id}/schedule`, { ...body, force }));
      if (res) {
        toast.success("Ride updated. Driver and customer have been notified.");
        onChanged();
      }
    } catch (e) {
      const msg = errorText(e, "Failed to update ride");
      if (e.response?.status === 400 && msg === "No changes to save") toast.info(msg);
      else toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!editable) return <p className="text-sm text-slate-500">This ride can no longer be rescheduled.</p>;

  return (
    <div className="space-y-3" data-testid="reschedule-section">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label>Pickup date</Label><Input type="date" value={form.pickup_date} onChange={set("pickup_date")} data-testid="reschedule-date-input" /></div>
        <div className="space-y-1"><Label>Pickup time (ET)</Label><Input type="time" value={form.pickup_time} onChange={set("pickup_time")} data-testid="reschedule-time-input" /></div>
      </div>
      {hasReturn && (
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Return date</Label><Input type="date" value={form.return_pickup_date} onChange={set("return_pickup_date")} data-testid="reschedule-return-date-input" /></div>
            <div className="space-y-1"><Label>Return time (ET)</Label><Input type="time" value={form.return_pickup_time} disabled={form.return_time_tbc} onChange={set("return_pickup_time")} data-testid="reschedule-return-time-input" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox className={CHECKBOX_CLS} checked={form.return_time_tbc} onCheckedChange={(v) => setForm((f) => ({ ...f, return_time_tbc: !!v }))} data-testid="reschedule-return-tbc" />
            Return Time To Be Confirmed
          </label>
        </div>
      )}
      <div className="space-y-1"><Label>Trip notes (visible to driver)</Label><Textarea rows={2} value={form.special_requests} onChange={set("special_requests")} data-testid="reschedule-notes-input" /></div>
      <div className="space-y-1"><Label>Dispatch notes</Label><Textarea rows={2} value={form.dispatch_notes} onChange={set("dispatch_notes")} data-testid="reschedule-dispatch-notes-input" /></div>
      <Button onClick={save} disabled={busy} className="bg-cyan-600 hover:bg-cyan-700" data-testid="reschedule-save-btn">
        <CalendarClock className="w-4 h-4 mr-2" />Save Changes
      </Button>
    </div>
  );
};

export default RescheduleSection;
